import React, { useMemo } from "react";
import { CheckCircle2, ChefHat, Flame, Pizza, ShoppingBag, Truck, User } from "lucide-react";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import {
  painelService,
  type PainelFinalizadoItem,
} from "../../../services/painelService";
import { formatPainelDescricaoResumo } from "../utils/descricaoPainel";
import { nomesUnicos } from "../utils/agruparPedidosPainel";
import { PainelPedidoOriginalButton } from "./PainelPedidoOriginalButton";
import { PainelCardShell } from "./PainelCardShell";
import {
  isPedidoSalaoFoodfly,
  resolveSalaoProducaoLabels,
} from "../utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";

import { BrazilDate } from "../../../utils/BrazilDate";

interface CardPedidoFinalizadoProps {
  itens: PainelFinalizadoItem[];
  onVerPedidoOriginal?: (orderId: number) => void;
}

function horaBr(iso: string): string {
  try {
    const full = BrazilDate.formatBR(new Date(iso), true);
    const m = full.match(/\b(\d{2}:\d{2})/);
    return m?.[1] ?? "—";
  } catch {
    return "—";
  }
}

function dataBr(iso: string): string {
  try {
    return BrazilDate.formatBR(new Date(iso), false).slice(0, 5);
  } catch {
    return "—";
  }
}

function maxSegundo(
  valores: (number | null | undefined)[]
): number | null {
  const nums = valores.filter((v): v is number => v != null && v >= 0);
  return nums.length > 0 ? Math.max(...nums) : null;
}

export const CardPedidoFinalizado: React.FC<CardPedidoFinalizadoProps> = ({
  itens,
  onVerPedidoOriginal,
}) => {
  const principal = itens[0];
  const { displayId, orderId, origem } = principal;
  const isSalao = isPedidoSalaoFoodfly({
    canalOrigem: principal.canalOrigem,
  });
  const finalizadoEm = itens.reduce((latest, item) => {
    return new Date(item.finalizadoEm).getTime() >
      new Date(latest).getTime()
      ? item.finalizadoEm
      : latest;
  }, principal.finalizadoEm);

  const customerWeb = !isSalao
    ? principal.customerName ??
      itens.find((i) => i.customerName)?.customerName ??
      null
    : null;
  const montadores = useMemo(
    () => nomesUnicos(itens.map((i) => i.montadorNome)),
    [itens],
  );
  const salaoMesaId =
    principal.salaoMesaId ??
    itens.find((i) => i.salaoMesaId)?.salaoMesaId ??
    null;
  const salaoComandaId =
    principal.salaoComandaId ??
    itens.find((i) => i.salaoComandaId)?.salaoComandaId ??
    null;
  const salaoIdentificador =
    principal.salaoIdentificador ??
    itens.find((i) => i.salaoIdentificador)?.salaoIdentificador ??
    null;
  const salaoLabels = isSalao
    ? resolveSalaoProducaoLabels({
        mesaId: salaoMesaId,
        comandaId: salaoComandaId,
        identificador: salaoIdentificador,
        displayId,
      })
    : null;
  const headerBadges = isSalao ? (
    <SalaoProducaoOrderHeader
      labels={salaoLabels}
      showSalaoTag
      layout="inline"
      className="gap-1"
    />
  ) : undefined;

  const descricoes = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of itens) {
      const d = item.descricao?.trim();
      if (d && !seen.has(d)) {
        seen.add(d);
        out.push(d);
      }
    }
    return out;
  }, [itens]);

  const aguardandoEntregaSeg =
    origem === "entrega"
      ? maxSegundo(itens.map((i) => i.tempos.aguardandoEntregaSeg))
      : null;
  const emEntregaSeg =
    origem === "entrega"
      ? maxSegundo(itens.map((i) => i.tempos.emEntregaSeg))
      : null;
  const mostrarTemposEntrega =
    origem === "entrega" &&
    (aguardandoEntregaSeg != null || emEntregaSeg != null);

  const origemTexto =
    origem === "entrega"
      ? "Entrega finalizada"
      : origem === "balcao"
        ? "Pronto no balcão"
        : "Saída do forno (retirada)";
  const OrigemIcon =
    origem === "entrega" ? Truck : origem === "balcao" ? ShoppingBag : Flame;
  const temposLinha = [
    aguardandoEntregaSeg != null
      ? `Aguardando entrega ${painelService.formatSegundosParaBadge(aguardandoEntregaSeg)}`
      : null,
    emEntregaSeg != null
      ? `Em entrega ${painelService.formatSegundosParaBadge(emEntregaSeg)}`
      : null,
  ].filter((linha): linha is string => linha != null);
  const montadorTexto =
    montadores.length === 0
      ? null
      : montadores.length === 1
        ? montadores[0]
        : montadores.join(", ");

  const headerEnd = (
    <>
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-emerald-300 bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-900">
        <CheckCircle2 size={11} />
        {dataBr(finalizadoEm)} {horaBr(finalizadoEm)}
      </span>
      {!isSalao && (
        <OrderTypeBadge
          orderType={principal.orderType}
          iconSize={12}
          withTooltip={false}
        />
      )}
    </>
  );

  return (
    <PainelCardShell
      variant="finalizado"
      displayChip="finalizado"
      dense
      isSalao={isSalao}
      displayId={displayId}
      headerBadges={headerBadges}
      badgeLabel={
        !isSalao ? `Pedido #${displayId} · ${dataBr(finalizadoEm)}` : undefined
      }
      headerEnd={headerEnd}
      headerClassName="mb-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1"
    >
      <div className="mb-1 flex flex-wrap items-center gap-1">
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${
            origem === "entrega"
              ? "bg-blue-100 text-blue-900"
              : origem === "balcao"
                ? "bg-violet-100 text-violet-900"
                : "bg-orange-100 text-orange-900"
          }`}
        >
          <OrigemIcon size={11} />
          {origemTexto}
        </span>
        {itens.length > 1 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-1.5 py-0.5 text-[10px] font-bold leading-none text-neutral-700">
            <Pizza size={11} />
            {itens.length} pizzas
          </span>
        )}
      </div>

      {descricoes.length > 0 && (
        <div className="mb-1 space-y-0.5">
          {descricoes.map((descricao, idx) => (
            <p
              key={idx}
              className="text-xs font-semibold leading-snug text-neutral-800 [[data-salao-card]_&]:text-white"
            >
              {formatPainelDescricaoResumo(descricao)}
            </p>
          ))}
        </div>
      )}

      {(customerWeb || montadorTexto) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs leading-snug text-slate-600 [[data-salao-card]_&]:text-white/90">
          {customerWeb && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <User size={12} className="shrink-0" />
              <span className="truncate">{customerWeb}</span>
            </span>
          )}
          {montadorTexto && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <ChefHat size={12} className="shrink-0" />
              <span className="truncate">{montadorTexto}</span>
            </span>
          )}
        </div>
      )}

      {(mostrarTemposEntrega || (onVerPedidoOriginal && orderId > 0)) && (
        <div className="mt-1 flex items-center justify-between gap-2">
          {temposLinha.length > 0 ? (
            <p className="min-w-0 text-[11px] leading-snug text-slate-600 [[data-salao-card]_&]:text-white/80">
              {temposLinha.join(" · ")}
            </p>
          ) : (
            <span />
          )}
          {onVerPedidoOriginal && orderId > 0 && (
            <PainelPedidoOriginalButton
              compact
              orderId={orderId}
              onOpen={onVerPedidoOriginal}
            />
          )}
        </div>
      )}
    </PainelCardShell>
  );
};
